import { useMemo, useState } from "react"
import {
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography,
} from "antd"
import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import classNames from "classnames"

import { DeleteConfirm } from "@src/app/components/DeleteConfirm"
import { notify } from "@src/app/components/notify"
import { CONTACTS_OPTIONS } from "@src/pages/registration/constants"
import css from "@src/pages/registration/ui/index.module.css"
import { useBreakpoint } from "@src/shared/hooks/useBreakpoints"

type Props = {
  form: FormInstance
  required: boolean
}
export const RegistrationFormContacts = ({ form, required }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [contactToDelete, setContactToDelete] = useState(0)
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const contacts =
    Form.useWatch<
      {
        type: string
        value: string
      }[]
    >(["details", "contacts"], form) ?? []

  const { smallerThanLg } = useBreakpoint()

  const showDeleteConfirm = (contact: number) => {
    setContactToDelete(contact)
    setOpenDeleteConfirm(true)
  }

  const getPlaceholder = (type: string = "") => {
    if (!type) {
      return `Enter value`
    }

    return `Enter your ${type} value`
  }

  const onDeleteConfirm = (callback: (contact: number) => void) => {
    callback(contactToDelete)
    notify.success("Contact has been deleted")
    setOpenDeleteConfirm(false)
  }

  const capitalizeFirstLetter = (str: string) => {
    return String(str).charAt(0).toUpperCase() + String(str).slice(1)
  }

  const getOptions = useMemo(() => {
    const pickedTypes = contacts.map(({ type }) => type)

    return CONTACTS_OPTIONS.filter(
      (option) => !pickedTypes.includes(option),
    ).reduce((acc: Record<string, string>[], i) => {
      acc.push({ label: capitalizeFirstLetter(i), value: i })
      return acc
    }, [])
  }, [contacts])

  return (
    <Flex
      style={{ borderBottom: "1px solid #E9F1EA", paddingBottom: 16 }}
      vertical
    >
      <Typography.Text
        className={classNames({ [css.contactsText as string]: required })}
      >
        Contact details
      </Typography.Text>
      <Form.List
        initialValue={[{ type: CONTACTS_OPTIONS[0] }]}
        name={["details", "contacts"]}
      >
        {(fields, { add, remove }) => (
          <Flex gap={16} style={{ width: "100%" }} vertical>
            {fields.map(({ key, name, ...restField }) => (
              <Row
                justify={smallerThanLg ? "center" : "space-between"}
                key={key}
                style={{ width: "100%" }}
              >
                <Col lg={8} xs={8}>
                  <Form.Item
                    {...restField}
                    id="GENERATED_ID_REGISTRATIONFORMCONTACTS_TSX_FORM.ITEM_VOID_1"
                    name={[name, "type"]}
                    rules={[
                      {
                        message: "Please select at least one",
                        required,
                      },
                    ]}
                  >
                    <Select
                      labelRender={(v) => (
                        <Typography.Text>
                          {capitalizeFirstLetter(v.value as string)}
                        </Typography.Text>
                      )}
                      loading={isLoading}
                      options={getOptions}
                    />
                  </Form.Item>
                </Col>
                <Col lg={11} style={{ padding: "0 8px" }} xs={12}>
                  <Form.Item
                    {...restField}
                    dependencies={[name, "type"]}
                    id="GENERATED_ID_REGISTRATIONFORMCONTACTS_TSX_FORM.ITEM_VOID_2"
                    name={[name, "value"]}
                    rules={[
                      {
                        message: "Please input correct info",
                        required,
                      },
                    ]}
                  >
                    <Input placeholder={getPlaceholder(contacts[name]?.type)} />
                  </Form.Item>
                </Col>
                <Col lg={5} md={4} span={4} style={{ paddingRight: 0 }}>
                  <Flex justify="end" style={{ width: "100%" }}>
                    {key === 0 ? (
                      <Button
                        disabled={!getOptions.length}
                        htmlType="button"
                        icon={<PlusCircleOutlined />}
                        id="GENERATED_ID_REGISTRATIONFORMCONTACTS_BUTTON_ADD_1"
                        loading={isLoading}
                        onClick={() => add({ type: getOptions[0]?.value })}
                        style={{ width: "100%" }}
                      >
                        {smallerThanLg ? "" : "Add"}
                      </Button>
                    ) : (
                      <Button
                        icon={<MinusCircleOutlined />}
                        id="GENERATED_ID_REGISTRATIONFORMCONTACTS_BUTTON_VOID_1"
                        onClick={() => showDeleteConfirm(name)}
                        style={{ maxHeight: "44px", width: "100%" }}
                      ></Button>
                    )}
                  </Flex>
                </Col>
              </Row>
            ))}
            <DeleteConfirm
              cancelText="Cancel"
              isLoading={isLoading}
              okText="Delete Contact"
              onCancel={() => setOpenDeleteConfirm(false)}
              onOk={() => onDeleteConfirm(remove)}
              open={openDeleteConfirm}
              text="This action cannot be undone"
              title="Are you sure you want to delete this contact?"
            />
          </Flex>
        )}
      </Form.List>
    </Flex>
  )
}
